import { AppointmentSuggestion } from "@/data/models/AppointmentSuggestion";
import { FocusedTask, ScheduledTask } from "@/data/models/Task";
import { TaskScheduler } from "./TaskScheduler";
import { SchedulingModule } from "@/store/modules/SchedulingModule";
import { generateRandomUuid } from "../Utils";
import { calculateObjectiveFunctionRelativeToFocusedTask } from "./SchedulingObjectiveFunction";
import { DateTime } from "luxon";
import { getDateFromYearMonthDateString } from "../DateHelper";

export class AppointmentSuggester {
  private findNextFittingTimeSlot(
    focusedTask: FocusedTask,
    targetUserId: string,
    alreadySuggested: ScheduledTask[]
  ) {
    const scheduler = new TaskScheduler(
      [
        ...SchedulingModule.scheduledTasks.filter(
          (schedueledTask) => schedueledTask.id !== focusedTask.id
        ),
        ...alreadySuggested,
      ],
      SchedulingModule.sortedUsers,
      SchedulingModule.userLookup,
      SchedulingModule.schedulingSettings
    );

    return scheduler.scheduleTasks(
      [focusedTask],
      !SchedulingModule.timelineSettings.showWeekends,
      {
        forcedUserId: targetUserId,
      }
    )[0];
  }

  suggestAppointment(
    focusedTask: FocusedTask,
    alreadySuggested: ScheduledTask[]
  ): AppointmentSuggestion | null {
    const { targetDate, isTargetDateFixed } = focusedTask.planningParameters;

    const defaultStartingFrom = new Date();

    if (targetDate && isTargetDateFixed) {
      const isTargetDateBeforeToday =
        DateTime.fromJSDate(defaultStartingFrom)
          .startOf("day")
          .valueOf() >
        (getDateFromYearMonthDateString(targetDate)?.getTime() ??
          Number.MAX_SAFE_INTEGER);

      if (isTargetDateBeforeToday) {
        return null;
      }
    }

    const userLookup = SchedulingModule.userLookup;

    const scheduler = new TaskScheduler(
      [],
      SchedulingModule.sortedUsers,
      userLookup,
      SchedulingModule.schedulingSettings
    );

    const users = scheduler.getPossibleUsersForTask(focusedTask);

    let bestResult: AppointmentSuggestion | null = null;

    for (const user of users) {
      const fittingScheduledTask = this.findNextFittingTimeSlot(
        focusedTask,
        user.id,
        alreadySuggested
      );

      if (fittingScheduledTask) {
        const scheduledTask: ScheduledTask = {
          ...fittingScheduledTask.scheduledTask,
          id: generateRandomUuid(),
        };

        const score = calculateObjectiveFunctionRelativeToFocusedTask(
          scheduledTask
        );

        if (score) {
          scheduledTask.schedulingScore = score;

          const appointmentSuggestion: AppointmentSuggestion = {
            suggestionIndex: alreadySuggested.length,
            scheduledTask: scheduledTask,
            schedulingScore: score,
          };

          if (!bestResult) {
            bestResult = appointmentSuggestion;
          } else {
            const isImprovement =
              score.total > bestResult.schedulingScore.total;

            const hasSameScoreButIsMoreRecent =
              score.total === bestResult.schedulingScore.total &&
              bestResult.scheduledTask.startDate.getTime() >
                appointmentSuggestion.scheduledTask.startDate.getTime();

            if (isImprovement || hasSameScoreButIsMoreRecent) {
              bestResult = appointmentSuggestion;
            }
          }
        }
      }
    }

    return bestResult;
  }
}
